import React, { useEffect, useCallback, useState, useRef } from 'react';
import { SwitchCamera } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import Camera, { FACING_MODES, IMAGE_TYPES } from "react-html5-camera-photo";
import 'react-html5-camera-photo/build/css/index.css';
import back from "../../assets/Icons/Back-white.png"
import demoIcon from "../../assets/Icons/image-reference.png"
import teethListIcon from "../../assets/Icons/teeth-list-icon.png"
import flashOn from "../../assets/Icons/flash-on.png"
import flashOff from "../../assets/Icons/flash-off.png"
import DemoScreens from './demoScreens';
import Preview from './preview';
import "./index.css"
import { generateSteps } from "./steps";
import PermissionPopup from './cameraPermissionPopup';
import { GetBrowserDetails } from '../../Utils/deviceDetails';
import { GetMobileOperatingSystem } from '../../Utils/getOS';
import { fontStyle, fontWeight, companyBucket } from "../../Utils/tpscanStyle"
import FrontCameraError from './frontCameraError';
import { postLogDetails } from '../../Services/APIs/appDetails';
import teethSides from '../../Utils/teethSideNames';
import { notifyCameraError } from '../../Services/APIs/scan';
import { getCompleteDeviceDetails } from '../../Utils/comepleteDeviceInfo';
import Overlapper from '../StarInsurance/Scan/overlapper';
import PreviewFace from '../StarInsurance/Scan/previewFace';
import { flipImageHorizontally } from '../../Utils/imageTransform';

const Shoot = (props) => {
    const { activeImageSide, setToothPicker, quickScan, setImageSide } = props;
    const navigate = useNavigate();
    const OS = GetMobileOperatingSystem();
    const uid = JSON.parse(localStorage.getItem("usertpi") || '{}').usertpi;
    const app = JSON.parse(localStorage.getItem("companyBucket") || "{}").companyBucket;

    // Get stored camera preference
    const storedCameraSide = localStorage.getItem("cameraSide") || "front";
    const [cameraSide, setCameraSide] = useState(storedCameraSide);
    const steps = generateSteps(cameraSide);

    // Initialize camera state
    const [facialMode, setFacialMode] = useState(
        storedCameraSide === "back" ? FACING_MODES.ENVIRONMENT : FACING_MODES.USER
    );
    const [frontCameraError, setFrontCameraError] = useState(false);
    const [backCameraError, setBackCameraError] = useState(false);
    const [isDemo, setDemo] = useState(true);
    const [isCamera, setCamera] = useState(true);
    const [capturedImage, setCapturedImage] = useState();
    const [uploadImage, setUploadImage] = useState();
    const [flashLight, setFlashLight] = useState(false);
    const [mediaStream, setStream] = useState(null);
    const [errorType, setErrorType] = useState({ isError: false, type: "ios" });
    const [isMirrored, setIsMirrored] = useState(storedCameraSide !== "back");
    const [showFlipHint, setShowFlipHint] = useState(false);
    const initializedRef = useRef(false); // Ref to track initialization across renders

    // Log camera operation
    const logCameraOperation = useCallback((message) => {
        postLogDetails({ uid, app, payLoad: message });
    }, [uid, app]);

    // Handle camera errors
    const handleCameraError = (error) => {
        logCameraOperation(`Camera error: ${JSON.stringify(error)}`);
        notifyCameraError({
            info: {
                ...getCompleteDeviceDetails(),
                uid,
                app,
                error: JSON.stringify(error)
            }
        });
    };


    // iOS front camera handler
    const handleWithFacialMode = () => {
        navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: facialMode,
                width: { ideal: window.innerWidth },
                height: { ideal: window.innerHeight }
            }
        }).then((stream) => {
            setStream(stream);
            logCameraOperation(`iOS - found front camera, video-track-length-${stream.getVideoTracks().length}`);
        }).catch((error) => {
            initializedRef.current = false;
            handleCameraError(error);
            if (storedCameraSide === "back") {
                setBackCameraError(true);
            } else {
                setFrontCameraError(true);
            }
            logCameraOperation(`iOS - ${storedCameraSide} camera error`);
        });

    };

    // Android camera handler - uses device enumeration
    const handleWithLabel = () => {
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const videoDevices = devices.filter(device => device.kind === 'videoinput');

                const frontCamera = videoDevices.find(device => device.getCapabilities().facingMode?.includes("user"));
                if (frontCamera) {
                    setFacialMode(frontCamera.deviceId); // Set the deviceId directly
                    postLogDetails({ uid, app, payLoad: "Android - found front camera" })

                } else {
                    postLogDetails({ uid, app, payLoad: `Android- front camera error  ${JSON.stringify(videoDevices)} so starting rear-camera` })
                    notifyCameraError({ info: { ...getCompleteDeviceDetails(), uid, app, error: JSON.stringify(videoDevices) } })
                    setFacialMode(FACING_MODES.ENVIRONMENT); // Fallback to rear camera
                    setFrontCameraError(true)
                }
            })
            .catch(err => {
                initializedRef.current = false;
                postLogDetails({ uid, app, payLoad: `Android- media devices error ${JSON.stringify(err)}` })
            });
    }



    // Handle camera type selection based on OS
    const handleCameraType = () => {
        // Don't reinitialize camera if we've already set it up
        if (initializedRef.current) {
            logCameraOperation(`Camera already initialized, skipping handleCameraType`);
            return;
        }

        // Mark as initialized using the ref
        initializedRef.current = true;

        if (facialMode === FACING_MODES.ENVIRONMENT) {
            logCameraOperation(`Explicitly initializing BACK camera`);
            handleWithFacialMode();
        } else {
            OS === "iOS" ? handleWithFacialMode() : handleWithLabel();
        }
    };

    const cameraStarted = (stream) => {
        setStream(stream);
        handleCameraType();
        setErrorType({ isError: false, type: "ios" });
    };

    // Stop camera
    const stopCamera = useCallback(() => {
        if (mediaStream) {
            mediaStream.getTracks().forEach((track) => {
                logCameraOperation(`Stopping track with kind: ${track.kind}, readyState: ${track.readyState}`);
                track.stop();
                logCameraOperation(`Track stopped ${track.kind}, readyState: ${track.readyState}`);
            });
            setStream(null);
        }
    }, [mediaStream]);

    useEffect(() => {
        return () => { stopCamera(); };
    }, []);

    // Show the "Flip camera" hint briefly each time the live camera view appears
    useEffect(() => {
        if (!isCamera) return;
        setShowFlipHint(true);
        const timer = setTimeout(() => setShowFlipHint(false), 4500);
        return () => clearTimeout(timer);
    }, [isCamera]);

    // Force front camera when in face scan mode (activeImageSide === -1)
    useEffect(() => {
        if (activeImageSide === -1 && cameraSide === "back") {
            localStorage.setItem("cameraSide", "front");
            initializedRef.current = false;
            setFrontCameraError(false);
            setBackCameraError(false);
            setCameraSide("front");
            setFacialMode(FACING_MODES.USER);
            setIsMirrored(true);
            stopCamera();
        }
    }, [activeImageSide, cameraSide, stopCamera]);

    // Restart camera
    // const restartCamera = () => {
    //     stopCamera();
    //     handleCameraType();
    // };

    // Handle photo capture
    const handleTakePhoto = async (dataUri) => {
        const base64String = dataUri.split(',')[1];
        const estimatedSizeInBytes = (base64String.length * 3) / 4;
        const estimatedSizeInKB = estimatedSizeInBytes / 1024;
        const estimatedSizeInMB = estimatedSizeInKB / 1024;

        const flippedUri = cameraSide === "back" ? await flipImageHorizontally(dataUri) : dataUri;

        setCapturedImage(dataUri);
        setUploadImage(flippedUri);
        logCameraOperation(`captured ${teethSides[activeImageSide]} teeth, ${estimatedSizeInMB.toFixed(5)} MB`);

        setTimeout(() => {
            setCamera(false);
        }, 400);

        stopCamera();
    };

    // Check for camera permission errors
    const checkError = () => {
        const browser = GetBrowserDetails();

        if (browser.name === "Mobile Chrome") {
            navigator.permissions.query({ name: 'camera' }).then((res) => {
                if (res.state === "denied") {
                    if (OS === 'iOS') {
                        setErrorType({ isError: true, type: "ios" });
                    } else {
                        setErrorType({ isError: true, type: "android" });
                    }
                }
            }).catch((error) => {
                setErrorType({ isError: true, type: "ios" });
            });
        } else {
            setErrorType({ isError: true, type: "ios" });
        }
    };

    // Ovule shape component
    const OvuleShape = () => {
        return (
            <div className={`ovule-container w-full flex flex-col justify-center items-center `}>
                <div
                    className={`relative ovule ovule-${activeImageSide} ${activeImageSide === 4 &&
                        isCamera &&
                        cameraSide !== "back" &&
                        "ovule-4-reversed"
                        }`}
                >
                    <p
                        className={`absolute -bottom-10 w-full text-center text-base text-white ${activeImageSide === 4 && isCamera && cameraSide !== "back" && "transform rotate-180"} `}
                        style={{ ...fontWeight, ...fontStyle }}>
                        {steps[activeImageSide].title}
                    </p>
                </div>
            </div>
        );
    };

    const handleCameraSwitch = () => {
        const newSide = cameraSide === "back" ? "front" : "back";
        localStorage.setItem("cameraSide", newSide);
        initializedRef.current = false;
        setFrontCameraError(false);
        setBackCameraError(false);
        setCameraSide(newSide);
        setFacialMode(newSide === "back" ? FACING_MODES.ENVIRONMENT : FACING_MODES.USER);
        setIsMirrored(newSide !== "back");
        stopCamera();
        if (activeImageSide === 4) {
            setDemo(true);
        }
    };

    // Top layer component
    const TopLayer = () => {
        return (
            <div className='camera-top-layer-2 h-full absolute top-0 text-white w-full'>
                <div className='flex justify-between items-center h-13 px-2'>
                    <button type='button' onClick={() => { localStorage.getItem("isAutomated") ? window.history.back() : setToothPicker(true) }}>
                        <img src={back} alt="back" className="w-8 h-8" />
                    </button>
                    <div className='pt-2 flex items-center'>

                        <button type='button' onClick={() => { setDemo(true) }}>
                            <img src={demoIcon} alt="back" className="w-12 h-12" />
                        </button>
                    </div>
                </div>
                <p
                    className={`text-base mt-16 text-center ${activeImageSide === 4 && isCamera && cameraSide !== "back" && "transform rotate-180"}`}
                    style={{ ...fontWeight, ...fontStyle }}>
                    {isCamera ? "Tap anywhere to capture" : `Preview of the ${steps[activeImageSide].title}`}
                </p>

            </div>
        );
    };

    // Display back camera error message
    const BackCameraError = ({ back }) => {
        return (
            <div className="absolute top-0 w-full bg-black bg-opacity-80 p-4 text-white">
                <p>Back camera could not be activated. Using front camera instead.</p>
                <button
                    className="mt-2 px-4 py-2 bg-blue-500 rounded"
                    onClick={() => back(false)}
                >
                    Dismiss
                </button>
            </div>
        );
    };

    return (
        <>
            {isDemo ? (
                <DemoScreens
                    activeImageSide={activeImageSide}
                    setToothPicker={setToothPicker}
                    setDemo={setDemo}
                    setCamera={setCamera}
                    quickScan={quickScan}
                />
            ) : (
                <div>
                    {errorType.isError &&
                        <PermissionPopup
                            info={errorType}
                            setErrorType={setErrorType}
                        />
                    }

                    {isCamera ? (
                        <>
                            <Camera
                                key={`camera-${isMirrored}`}
                                onTakePhoto={(dataUri) => { handleTakePhoto(dataUri); }}
                                idealFacingMode={facialMode}
                                isFullscreen={false}
                                isMaxResolution={OS === "iOS" ? false : true}
                                imageType={IMAGE_TYPES.JPG}
                                isImageMirror={isMirrored}
                                imageCompression={OS === "iOS" ? 0.5 : 0.6}
                                sizeFactor={1}
                                videoConstraints={{
                                    width: { ideal: window.innerWidth },
                                    height: { ideal: window.innerHeight },
                                    aspectRatio: { ideal: window.innerWidth / window.innerHeight },
                                    ...(facialMode !== FACING_MODES.USER && facialMode !== FACING_MODES.ENVIRONMENT
                                        ? { deviceId: { exact: facialMode } }
                                        : {})
                                }}
                                onCameraStart={cameraStarted}
                                onCameraError={checkError}
                                className="WebCam"
                            />
                            {!errorType.isError && frontCameraError &&
                                <FrontCameraError back={setFrontCameraError} />
                            }
                            {!errorType.isError && backCameraError && storedCameraSide === "back" &&
                                <BackCameraError back={setBackCameraError} />
                            }
                        </>
                    ) : (
                        <>
                            {activeImageSide === -1 ? (
                                <PreviewFace
                                    setImageSide={setImageSide}
                                    image={capturedImage}
                                    setCamera={setCamera}
                                    setToothPicker={setToothPicker}
                                    activeImageSide={activeImageSide}
                                    setDemo={setDemo}
                                    quickScan={quickScan}
                                />
                            ) : (
                                <Preview
                                    setImageSide={setImageSide}
                                    image={capturedImage}
                                    uploadImage={uploadImage}
                                    setCamera={setCamera}
                                    setToothPicker={setToothPicker}
                                    activeImageSide={activeImageSide}
                                    setDemo={setDemo}
                                    quickScan={quickScan}
                                />
                            )}
                        </>
                    )}

                    {activeImageSide === -1 ? (
                        isCamera ? <Overlapper /> : null
                    ) : (
                        <>
                            <OvuleShape />
                            <TopLayer />
                            {isCamera && (
                                <div className="absolute bottom-4 right-4 z-10 flex flex-row items-center">
                                    <span
                                        className={`mr-2 px-4 py-2 rounded-full bg-black bg-opacity-70 text-white text-sm whitespace-nowrap transition-opacity duration-500 ${showFlipHint ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                                        style={{ ...fontStyle }}
                                    >
                                        Flip camera
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleCameraSwitch}
                                        className="w-[54px] h-[54px] flex items-center justify-center bg-black rounded-full"
                                    >
                                        <SwitchCamera size={25} color="white" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default Shoot;